import { currentLegacyIncentive, type LegacyIncentive } from './legacy-incentive';

/**
 * The incentive a campaign link names with its `iid` code. Landing pages read
 * the same code to swap the incentive they advertise and forward it unchanged
 * on the signup link, so signup shows what the landing page showed.
 *
 * The codes are made up on purpose so a visitor cannot guess another one from
 * its name. They are not secret: anyone can copy a link. The landing pages
 * keep their own copy of this list (growth-landings
 * `src/app/services/incentiveOverride.ts`), so add a code in both places.
 *
 * This changes messaging only. What a member can earn still comes from the
 * sponsor's own setup, and no Galapagos call or event carries the code (the
 * page address analytics records may still show it).
 */
export type LinkIncentive = 'monetary' | 'gifts' | 'gifts-oura' | 'none';

export const LINK_INCENTIVE_PARAM = 'iid';

const LINK_INCENTIVE_CODES: Readonly<Record<string, LinkIncentive>> = {
  // The $75 gift card.
  jvk5fz: 'monetary',
  // Gifts led by the Theragun, no Oura Ring.
  '7sz9nb': 'gifts',
  // Gifts led by the Oura Ring.
  '5ajs2a': 'gifts-oura',
  // No incentive.
  u8d5r5: 'none',
};

// A campaign code, not a member answer, so it sits outside the flow store's
// stored answers and outlives a flow that starts over in the same tab.
const LINK_INCENTIVE_STORAGE_KEY = 'signup.iid';

function codeOf(raw: string | null | undefined): string | undefined {
  // Email tools and link wrappers do not reliably keep case.
  const code = raw?.trim().toLowerCase();
  return code && Object.prototype.hasOwnProperty.call(LINK_INCENTIVE_CODES, code)
    ? code
    : undefined;
}

/** The incentive an `iid` value names; undefined when missing, empty or unknown. */
export function parseLinkIncentive(raw: string | null | undefined): LinkIncentive | undefined {
  const code = codeOf(raw);
  return code === undefined ? undefined : LINK_INCENTIVE_CODES[code];
}

function readStoredCode(): string | null {
  try {
    return window.sessionStorage.getItem(LINK_INCENTIVE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeCode(code: string): void {
  try {
    window.sessionStorage.setItem(LINK_INCENTIVE_STORAGE_KEY, code);
  } catch {
    // Storage unavailable (private mode, quota): the URL still carries the code.
  }
}

/**
 * The visit's incentive, as `captureLinkIncentive` would answer, without
 * remembering the URL's code: for readers that run often and must not write.
 */
export function readLinkIncentive(search: string): LinkIncentive | undefined {
  if (typeof window === 'undefined') return undefined;
  const fromUrl = codeOf(new URLSearchParams(search).get(LINK_INCENTIVE_PARAM));
  if (fromUrl !== undefined) return LINK_INCENTIVE_CODES[fromUrl];
  return parseLinkIncentive(readStoredCode());
}

/**
 * The visit's incentive: the URL's code when it is a known one (remembered for
 * the rest of the tab session), else the code remembered earlier. An unknown
 * code in the URL leaves a remembered one alone.
 */
export function captureLinkIncentive(search: string): LinkIncentive | undefined {
  if (typeof window === 'undefined') return undefined;
  const fromUrl = codeOf(new URLSearchParams(search).get(LINK_INCENTIVE_PARAM));
  if (fromUrl !== undefined) {
    storeCode(fromUrl);
    return LINK_INCENTIVE_CODES[fromUrl];
  }
  return parseLinkIncentive(readStoredCode());
}

/**
 * Whether the sponsor's rewards switch counts for this visit. A code never
 * turns rewards on; the no-incentive code turns them off for every sponsor.
 */
export function sponsorRewardsOn(
  rewards: boolean | undefined,
  incentive: LinkIncentive | undefined,
): boolean {
  return incentive !== 'none' && rewards === true;
}

/**
 * The booking step's link-dated gift-card reminder for this visitor, or null:
 * the no-incentive code hides it with every other rewards surface.
 */
export function currentBookingIncentive(search: string): LegacyIncentive | null {
  if (captureLinkIncentive(search) === 'none') return null;
  return currentLegacyIncentive(search);
}
